package dev.kuml.portal.web.editor

/**
 * R-18 §5.1 -- survives the full editor remount a language switch causes
 * ([dev.kuml.portal.web.PortalApp.renderScreen]'s KDoc) so a half-typed prompt is not silently
 * destroyed by an unrelated UI action (switching the interface language). Keyed by diagram id so a
 * draft can never resurface in the wrong diagram; [consume] returns it exactly once and clears it,
 * so a later mount of the SAME diagram (a real navigation, not a language-switch remount) never
 * unexpectedly refills the prompt field with stale text.
 *
 * Deliberately a single, process-wide slot (like [dev.kuml.portal.web.i18n.LanguageState]) rather
 * than a map of all open diagrams -- only ONE diagram is ever mounted at a time in this SPA, so a
 * single `(diagramId, text)` pair is exactly as much state as is ever needed.
 */
public object PromptDraft {
    private var entry: Pair<String, String>? = null

    /** Blank [text] clears the draft outright (nothing worth restoring). */
    public fun remember(
        diagramId: String,
        text: String,
    ) {
        entry = if (text.isBlank()) null else diagramId to text
    }

    /** Returns and clears the draft for [diagramId], or `null` if there is none (including: the
     * stored draft belongs to a DIFFERENT diagram -- never leaks across diagrams). */
    public fun consume(diagramId: String): String? {
        val current = entry ?: return null
        if (current.first != diagramId) return null
        entry = null
        return current.second
    }
}
